import React from 'react';
import { Separator } from '@/components/ui/separator';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Logo } from '@/components/logo';
import { LanguageSwitcher } from '@/components/language-switcher';
import Facebook from '@/images/social/facebook-logo.png';
import Instagram from '@/images/social/instagram-logo.png';
import LinkedIn from '@/images/social/linkedin-logo.png';
import { cn } from '@/lib/utils';
export const SiteFooter = () => {
    const { t } = useTranslation('marketing');
    const year = new Date().getFullYear();
    return (<footer className="border-t bg-background">
            <div className="mx-auto max-w-7xl px-4 md:px-6 lg:px-8 py-10 space-y-8">
                {/* ================= BRAND + NAV ================= */}
                <div className="grid gap-6 md:grid-cols-[minmax(0,2fr)_minmax(0,4fr)]">
                    {/* Brand */}
                    <div className="space-y-3">
                        <Logo to="/"/>
                        <p className="text-xs text-muted-foreground max-w-sm">
                            {t('footer.tagline', 'Understand what really works for your offline and online campaigns.')}
                        </p>

                        <LanguageSwitcher />

                        {/* Social */}
                        <div className="flex items-center gap-3 pt-2">
                            <Social href="https://adora.media/c/ep-m5jet7jmpyrd" label="Adora on Facebook" icon={Facebook}/>
                            <Social href="https://adora.media/c/ep-ezufr9h8x5q9" label="Adora on Instagram" icon={Instagram}/>
                            <Social href="https://adora.media/c/ep-zs66a35rjcdt" label="Adora on LinkedIn" icon={LinkedIn} className="w-10"/>
                        </div>
                    </div>

                    {/* Navigation */}
                    <nav aria-label="Footer navigation">
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-6 text-sm">
                            <FooterProduct />
                            <FooterLearn />
                            <FooterCompare />
                            <FooterCompany />
                            <FooterSupport />
                        </div>
                    </nav>
                </div>

                <Separator />

                {/* ================= BOTTOM BAR ================= */}
                <div className="flex flex-col-reverse md:flex-row md:items-center md:justify-between gap-3 text-[11px] text-muted-foreground">
                    <div>
                        © {year} Adora. {t('footer.rights', 'All rights reserved.')}
                    </div>
                    <div>
                        <span>{t('footer.location', 'Built in Cambodia 🇰🇭')}</span>
                    </div>
                </div>
            </div>
        </footer>);
};
/* ================= GROUPS ================= */
const FooterProduct = () => (<FooterGroup title="Product">
        <FooterLink to="/what-is-adora">What is Adora</FooterLink>
        <FooterLink to="/how-it-works">How it works</FooterLink>
        <FooterLink to="/features">Features</FooterLink>
        <FooterLink to="/pricing">Pricing</FooterLink>
        <FooterLink to="/case-studies">Case studies</FooterLink>
        <FooterLink to="/smart-link">Smart Links</FooterLink>
    </FooterGroup>);
const FooterLearn = () => (<FooterGroup title="Learn">
        <FooterLink to="/blog">Blog</FooterLink>
        <FooterLink to="/learn/offline-marketing-analytics">Offline marketing analytics</FooterLink>
        <FooterLink to="/learn/smart-links">Smart Links explained</FooterLink>
        <FooterLink to="/learn/qr-scan-tracking">QR scan tracking</FooterLink>
        <FooterLink to="/learn/one-link-engine">One-link engine</FooterLink>
        <FooterLink to="/learn/ab-testing">A/B testing for campaigns</FooterLink>
    </FooterGroup>);
const FooterCompare = () => (<FooterGroup title="Compare">
        <FooterLink to="/compare/linktree">Adora vs Linktree</FooterLink>
        <FooterLink to="/compare/heylink">Adora vs HeyLink</FooterLink>
        <FooterLink to="/compare/flowcode">Adora vs Flowcode</FooterLink>
    </FooterGroup>);
const FooterCompany = () => (<FooterGroup title="Company">
        <FooterLink to="/about-adora-media">About Adora</FooterLink>
        <FooterLink to="/privacy">Privacy policy</FooterLink>
        <FooterLink to="/terms">Terms of service</FooterLink>
        <FooterLink to="/status">System status</FooterLink>
    </FooterGroup>);
const FooterSupport = () => (<FooterGroup title="Support">
        <ExternalLink href="https://adora.media/c/ep-xmr8azz5gggk">Contact support</ExternalLink>
        <FooterLink to="/status">Service status</FooterLink>
    </FooterGroup>);
/* ================= PRIMITIVES ================= */
function FooterGroup({ title, children }) {
    return (<div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">{title}</h3>
            <ul className="space-y-1 text-muted-foreground">{children}</ul>
        </div>);
}
function FooterLink({ to, children }) {
    return (<li>
            <Link to={to} className="hover:text-foreground hover:underline">
                {children}
            </Link>
        </li>);
}
function ExternalLink({ href, children }) {
    return (<li>
            <a href={href} target="_blank" rel="noopener noreferrer" className="hover:text-foreground hover:underline">
                {children}
            </a>
        </li>);
}
function Social({ href, icon, label, className }) {
    return (<a href={href} target="_blank" rel="noopener noreferrer" aria-label={label} className="hover:opacity-80 transition">
            <img src={icon} alt={label} className={cn('h-8 w-8', className)}/>
        </a>);
}
